// src/data/methodVerdicts.js (s133, PHASE 5a — method recommender, DECISIONS #45). Dependency-light and pure:
// the city page, the gate (scripts/verify-method-verdicts.mjs) and the methodology page all call this one
// function, so what renders and what is checked cannot drift apart.
//
// Output is DATA CELLS, not prose: every string is a short fragment (verify-method-verdicts enforces under 40
// characters or under 6 words), because full sentences repeated across 271 pages are exactly what the
// template-share gate and Google's scaled-content review look for. The reasoning lives once, on
// /methodology#method.
//
// It invents nothing. Every verdict is computed from a figure already on the city page (the utility's own
// hardness, TDS, iron, manganese, sodium, chloride and disinfectant, and the NOAA normals), and every
// threshold and consequence is the site's existing one: FIELD_GUIDE rules in waterPanel.js and the
// HARDNESS_BANDS scale in water.js. A verdict always names the figure it rests on; where the utility
// publishes nothing usable, the verdict says so instead of guessing.
import { FIELD_GUIDE } from './waterPanel.js';
import { classifyHardness } from './water.js';
import { MONTHS } from '../components/climate.js';

export const CELL_MAX_CHARS = 39;

const cap = (f) => (f && f.s === 'captured' ? f : null);
const hi = (f) => (f ? f.avg ?? f.value ?? (Array.isArray(f.range) ? f.range[1] : null) : null);
const lo = (f) => (f ? f.avg ?? f.value ?? (Array.isArray(f.range) ? f.range[0] : null) : null);
const num = (x) => (x == null ? null : Number(x));
const fmt = (a, b) => (a != null && b != null && a !== b ? `${a} to ${b}` : `${b ?? a}`);
const rule = (key, v) => FIELD_GUIDE[key].rules.find((r) => r.when(v, {})) || null;

// Published hardness as [low, high] mg/L, or null (unpublished, qualitative, unlocated).
export function hardnessRange(city) {
  const s = city?.hardnessSourcing;
  if (!s) return null;
  if (s.shape === 'point') { const v = num(s.value ?? s.mgL ?? s.avg); return v == null ? null : [v, v]; }
  if (s.shape === 'range' && Array.isArray(s.range)) return [num(s.range[0]), num(s.range[1])];
  return null;
}

const r2 = (x) => Number(x); // the utility's figure as published, never rounded
const range = (a, b) => (a != null && b != null && a !== b ? `${r2(a)}\u2013${r2(b)}` : `${r2(b ?? a)}`);
const DIS = (t) => t.replace(/^uv \+ /, 'UV + ').replace(/^ozone \+ /, 'ozone + ');

// Each verdict: { key, label, verdict, basis, note, level } — all short fragments.
export function methodVerdicts(city, climate) {
  const p = city?.waterPanel || {};
  const out = [];
  const tds = cap(p.tds); const hr = hardnessRange(city);

  // 1. Final rinse — TDS first (FIELD_GUIDE.tds: it predicts spotting better than hardness), else hardness band.
  if (tds) {
    const v = num(hi(tds)); const level = v >= 300 ? 'pure' : v <= 100 ? 'tap' : 'dry';
    out.push({ key: 'rinse', label: 'Final rinse', level,
      verdict: level === 'pure' ? 'Pure water' : level === 'tap' ? 'Tap water' : 'Tap, squeegeed off wet',
      basis: `TDS ${range(num(lo(tds)), v)} mg/L`,
      note: level === 'pure' ? 'Spots on dry-down (300+)' : level === 'tap' ? 'Dries clean (100 or less)' : 'Between the 100 and 300 flags' });
  } else if (hr) {
    const b = classifyHardness(hr[1]); const level = b.key === 'soft' ? 'tap' : b.key === 'moderate' ? 'dry' : 'pure';
    out.push({ key: 'rinse', label: 'Final rinse', level,
      verdict: level === 'pure' ? 'Pure water' : level === 'tap' ? 'Tap water' : 'Tap, squeegeed off wet',
      basis: `Hardness ${range(hr[0], hr[1])} mg/L`,
      note: `${b.label.charAt(0)}${b.label.slice(1).toLowerCase()}; no TDS published` });
  } else {
    out.push({ key: 'rinse', label: 'Final rinse', level: 'unknown', verdict: 'Test one pane first',
      basis: 'No hardness or TDS published', note: 'A white ring means pure water' });
  }

  // 2. Residue — metal stain beats salt beats mineral scale; 'clear' only when figures were published.
  const fe = cap(p.iron), mn = cap(p.manganese), na = cap(p.sodium), cl = cap(p.chloride);
  const feV = num(hi(fe)), mnV = num(hi(mn)), naV = num(hi(na)), clV = num(hi(cl));
  const feHit = feV != null && feV >= 0.3, mnHit = mnV != null && mnV >= 0.05;
  if (feHit || mnHit) {
    out.push({ key: 'residue', label: 'Residue', level: 'stain', verdict: feHit ? 'Iron staining' : 'Manganese staining',
      basis: feHit ? `Iron up to ${r2(feV)} mg/L` : `Manganese up to ${r2(mnV)} mg/L`,
      note: feHit ? 'Acid cleaners will not shift it' : 'Brown-black on sills and frames' });
  } else if ((naV != null && naV >= 60) || (clV != null && clV >= 100)) {
    const naHit = naV != null && naV >= 60;
    out.push({ key: 'residue', label: 'Residue', level: 'salt', verdict: 'Salt film',
      basis: naHit ? `Sodium up to ${r2(naV)} mg/L` : `Chloride up to ${r2(clV)} mg/L`,
      note: 'Pulls moisture back; rinse it off' });
  } else if (hr && !['soft', 'moderate'].includes(classifyHardness(hr[1]).key)) {
    out.push({ key: 'residue', label: 'Residue', level: 'mineral', verdict: 'Mineral scale',
      basis: `Hardness up to ${r2(hr[1])} mg/L`, note: 'Fresh spots lift with mild acid' });
  } else if (fe || mn || na || cl) {
    // Print the published figure closest to its own flag level, so the cell carries a number, not a claim.
    const cand = [['Iron', feV, 0.3], ['Manganese', mnV, 0.05], ['Sodium', naV, 60], ['Chloride', clV, 100]].filter(([, v]) => v != null);
    const [nm, v] = cand.sort((a, b) => b[1] / b[2] - a[1] / a[2])[0];
    out.push({ key: 'residue', label: 'Residue', level: 'clear', verdict: 'Nothing flagged',
      basis: `${nm} up to ${r2(v)} mg/L`, note: 'All stain and salt figures under flags' });
  }

  // 3. Pure-water gear — chloramine (FIELD_GUIDE.disinfectant) and resin yield against a 100 mg/L baseline.
  const dis = cap(p.disinfectant); const tv = tds ? num(hi(tds)) : null;
  const yieldNote = tv && tv > 100 ? `Resin yield ~${Math.round(10000 / tv)}% of 100 mg/L water` : null;
  if (dis && /chloramine/.test(dis.type || '')) {
    out.push({ key: 'resin', label: 'Pure-water gear', level: 'chloramine', verdict: 'Chloramine supply',
      basis: DIS(dis.type), note: yieldNote && yieldNote.length <= CELL_MAX_CHARS ? yieldNote : 'Wears resin faster' });
  } else if (yieldNote) {
    out.push({ key: 'resin', label: 'Pure-water gear', level: 'load', verdict: 'Heavy resin load', basis: `TDS up to ${r2(tv)} mg/L`, note: yieldNote });
  }

  // 4. Heat — only where it changes the method: normal highs of 90 F or more AND a pure-water rinse verdict.
  //    (The climate strip above already shows the best months; this cell does not repeat them.)
  if (climate) {
    const hotI = MONTHS.map((_, i) => i).filter((i) => climate.hi[i] >= 90);
    if (hotI.length && out[0].level === 'pure') {
      out.push({ key: 'heat', label: 'Hot months', level: 'heat',
        verdict: hotI.length > 1 ? `${MONTHS[hotI[0]]}\u2013${MONTHS[hotI[hotI.length - 1]]}` : MONTHS[hotI[0]],
        basis: 'Normal highs 90\u00b0F or more', note: 'Rinse dries early: shade first' });
    }
  }
  return out;
}
